import { useCallback, useEffect, useState } from 'react';
import { api } from '../../api/client';
import { toast } from 'sonner';

type Proposal = { id: string; lead_id: string; company_name: string | null; chosen_type: number | null; suggestions: unknown[]; content: string | null; version: number; status: string; updated_at: string };

const STATUS_LABEL: Record<string, string> = {
  draft: 'Rascunho', sent_email: 'Enviada por email', sent_whatsapp: 'Enviada por WhatsApp',
  sent_both: 'Enviada (email + WhatsApp)', accepted: 'Aceita', rejected: 'Rejeitada',
};

export default function Propostas() {
  const [list, setList] = useState<Proposal[]>([]);
  const [sel, setSel] = useState<Proposal | null>(null);
  const [content, setContent] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const r = await api.get<{ proposals: Proposal[] }>('/proposals');
    setList(r.proposals);
    if (r.proposals[0] && !sel) void open(r.proposals[0].id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sel]);
  useEffect(() => { load(); }, [load]);

  async function open(id: string) {
    const r = await api.get<{ proposal: Proposal }>(`/proposals/${id}`);
    setSel(r.proposal);
    setContent(r.proposal.content ?? '');
  }

  async function onSave() {
    if (!sel) return;
    setBusy(true);
    try {
      await api.patch(`/proposals/${sel.id}`, { content });
      toast.success('Proposta salva (nova versão).');
      await load();
    } catch (err) {
      toast.error((err as { message?: string }).message ?? 'Falha ao salvar');
    } finally {
      setBusy(false);
    }
  }

  async function onStatus(status: 'accepted' | 'rejected') {
    if (!sel) return;
    try {
      await api.patch(`/proposals/${sel.id}`, { status });
      toast.success(status === 'accepted' ? 'Marcada como aceita.' : 'Marcada como rejeitada.');
      await load();
    } catch (err) {
      toast.error((err as { message?: string }).message ?? 'Falha no status');
    }
  }

  async function onSendWhatsApp() {
    if (!sel) return;
    setBusy(true);
    try {
      const r = await api.post<{ ok: boolean }>('/messages/whatsapp/send', { leadId: sel.lead_id, proposalId: sel.id });
      toast.success(r.ok ? 'Proposta enviada pelo WhatsApp.' : 'Sem confirmação de envio.');
      await load();
    } catch (err) {
      toast.error((err as { message?: string }).message ?? 'Falha no envio por WhatsApp');
    } finally {
      setBusy(false);
    }
  }

  async function onSendEmail() {
    if (!sel) return;
    setBusy(true);
    try {
      const r = await api.post<{ sent: boolean; demo: boolean }>('/messages/email/send', { leadId: sel.lead_id, proposalId: sel.id });
      toast.success(r.demo ? 'Email em modo demo (SMTP não configurado, só log). Para enviar de verdade, configure o SMTP.' : 'Email enviado.');
      await load();
    } catch (err) {
      toast.error((err as { message?: string }).message ?? 'Falha no envio por email');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <header>
        <p className="text-xs font-semibold tracking-widest text-[var(--text-dim)] uppercase">Propostas</p>
        <h1 className="mt-1 text-xl font-semibold tracking-tight">Suas propostas</h1>
      </header>
      <div className="mt-4 grid gap-4 lg:grid-cols-[280px_1fr]">
        <aside className="rounded-lg border border-[var(--border)] bg-white text-sm">
          {list.length === 0 && <p className="p-4 text-[var(--text-dim)]">Nenhuma proposta. Abra um lead e use "Sugerir propostas (IA)".</p>}
          {list.map((p) => (
            <button key={p.id} onClick={() => open(p.id)}
              className={sel?.id === p.id ? 'block w-full border-b border-[var(--border)] p-3 text-left bg-[var(--bg-subtle)]' : 'block w-full border-b border-[var(--border)] p-3 text-left hover:bg-[var(--bg-subtle)]'}>
              <span className="block font-medium">{p.company_name ?? 'Lead'}</span>
              <span className="text-xs text-[var(--text-dim)]">{STATUS_LABEL[p.status] ?? p.status} · v{p.version}</span>
            </button>
          ))}
        </aside>
        <section className="rounded-lg border border-[var(--border)] bg-white">
          {!sel && <p className="p-6 text-sm text-[var(--text-dim)]">Selecione uma proposta na lista.</p>}
          {sel && (
            <div className="p-4">
              <header className="flex items-center justify-between gap-3">
                <div>
                  <h2 className="text-base font-semibold">{sel.company_name}</h2>
                  <p className="text-xs text-[var(--text-dim)]">{STATUS_LABEL[sel.status]} · versão {sel.version}</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button onClick={onSendWhatsApp} disabled={busy} className="h-8 rounded-md border border-[var(--border-strong)] px-2 text-xs font-medium hover:bg-[var(--bg-subtle)] disabled:opacity-40">Enviar WhatsApp</button>
                  <button onClick={onSendEmail} disabled={busy} className="h-8 rounded-md border border-[var(--border-strong)] px-2 text-xs font-medium hover:bg-[var(--bg-subtle)] disabled:opacity-40">Enviar email</button>
                  <button onClick={() => onStatus('accepted')} className="h-8 rounded-md border border-[var(--border-strong)] px-2 text-xs hover:bg-[var(--bg-subtle)]">Aceita</button>
                  <button onClick={() => onStatus('rejected')} className="h-8 rounded-md border border-[var(--border-strong)] px-2 text-xs hover:bg-[var(--bg-subtle)]">Rejeitada</button>
                </div>
              </header>
              <textarea value={content} onChange={(e) => setContent(e.target.value)} rows={16}
                className="mt-3 w-full rounded-lg border border-[var(--border-strong)] bg-white p-3 font-mono text-xs" />
              <button onClick={onSave} disabled={busy} className="mt-3 h-9 rounded-lg bg-[var(--accent)] px-4 text-sm font-semibold text-white hover:bg-[#c40a20] disabled:opacity-50">
                {busy ? 'Salvando...' : 'Salvar proposta'}
              </button>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
